import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { logout } from '@/store/slices/authSlice'
import { useI18n } from '@/i18n'
import CommandPalette from '@/components/common/CommandPalette'
import OfflineBanner from '@/components/common/OfflineBanner'
import authService from '@/services/authService'
import {
  FiHome,
  FiClock,
  FiUsers,
  FiCalendar,
  FiDollarSign,
  FiAward,
  FiGrid,
  FiBook,
  FiFileText,
  FiSettings,
  FiLogOut,
  FiMenu,
  FiX,
  FiBriefcase,
  FiCreditCard,
  FiShield,
  FiCheckSquare,
  FiPackage,
  FiUserMinus,
  FiMapPin,
  FiHeart,
  FiAlertTriangle,
  FiTrendingUp,
  FiUser,
  FiClipboard,
  FiChevronDown,
  FiChevronRight,
  FiStar,
  FiSearch,
  FiLock,
  FiInbox,
  FiTarget,
  FiBell,
  FiEdit,
  FiRefreshCw,
  FiRepeat,
  FiBarChart2,
  FiActivity,
  FiUpload,
  FiGift,
} from 'react-icons/fi'

// ---------------------------------------------------------------------------
// Role constants
// ---------------------------------------------------------------------------
// Friendly labels for the built-in system role codes (for the role badge tooltip)
const PRIMARY_ROLE_LABELS = {
  employee: '員工',
  department_manager: '部門主管',
  hr_manager: 'HR 主管',
  company_admin: '公司管理員',
  super_admin: '超級管理員',
}

const HR_ADMIN = ['hr_manager', 'super_admin', 'company_admin']
const MANAGER_UP = ['department_manager', 'hr_manager', 'super_admin', 'company_admin']
const ADMIN_ONLY = ['super_admin', 'company_admin']

// ---------------------------------------------------------------------------
// Available views based on user's role
// ---------------------------------------------------------------------------
const VIEW_DEFS = {
  personal: { key: 'personal', label: '個人', emoji: '👤', icon: FiUser },
  team:     { key: 'team',     label: '團隊', emoji: '👔', icon: FiUsers },
  company:  { key: 'company',  label: '公司', emoji: '🏢', icon: FiBriefcase },
  system:   { key: 'system',   label: '系統', emoji: '⚙️', icon: FiSettings },
}

function getAvailableViews(user) {
  const role = user?.role
  const hasSubordinates = !!user?.has_subordinates
  const views = [VIEW_DEFS.personal]

  if (MANAGER_UP.includes(role) || hasSubordinates) {
    views.push(VIEW_DEFS.team)
  }
  if (HR_ADMIN.includes(role)) {
    views.push(VIEW_DEFS.company)
  }
  if (HR_ADMIN.includes(role)) {
    views.push(VIEW_DEFS.system)
  }
  return views
}

// ---------------------------------------------------------------------------
// Menu structure — organized by view
//
// For views 1, 2, 4 — items is a flat list.
// For view 3 (company) — items contains sub-groups with their own collapsible UI.
// ---------------------------------------------------------------------------

const menuStructure = {
  personal: {
    label: '個人專區',
    flat: true,
    items: [
      { path: '/', icon: FiHome, label: '我的首頁' },
      { path: '/my-profile', icon: FiUser, label: '我的資料' },
      { path: '/my-attendance', icon: FiClock, label: '我的出勤', excludeRoles: ['super_admin'] },
      { path: '/attendance-corrections', icon: FiCheckSquare, label: '打卡補登', excludeRoles: ['super_admin'] },
      { path: '/my-leave', icon: FiCalendar, label: '我的請假' },
      { path: '/my-overtime', icon: FiClock, label: '我的加班' },
      { path: '/my-expenses', icon: FiCreditCard, label: '我的費用' },
      { path: '/my-travel', icon: FiMapPin, label: '我的差旅' },
      { path: '/my-payslip', icon: FiDollarSign, label: '我的薪資條', excludeRoles: ['super_admin'] },
      { path: '/my-rewards', icon: FiAward, label: '我的獎懲', excludeRoles: ['super_admin'] },
      { path: '/performance/my-review', icon: FiTrendingUp, label: '我的績效' },
      { path: '/training/my', icon: FiBook, label: '我的訓練' },
      { path: '/my-benefits', icon: FiGift, label: '我的福利' },
      { path: '/my-requests', icon: FiFileText, label: '我的申請' },
      { path: '/swap-requests', icon: FiRepeat, label: '換班申請' },
      { path: '/notifications', icon: FiBell, label: '通知中心' },
      { path: '/directory', icon: FiUsers, label: '員工目錄' },
      { path: '/calendar', icon: FiCalendar, label: '行事曆' },
      { path: '/announcements', icon: FiFileText, label: '公告' },
      { path: '/surveys', icon: FiClipboard, label: '員工問卷' },
      { path: '/health-checkups', icon: FiHeart, label: '健康檢查' },
    ],
  },

  team: {
    label: '團隊管理',
    flat: true,
    items: [
      { path: '/team', icon: FiHome, label: '主管首頁' },
      { path: '/approvals', icon: FiInbox, label: '待審中心' },
      { path: '/approvals/leave', icon: FiCalendar, label: '假單審批', indent: true },
      { path: '/approvals/overtime', icon: FiClock, label: '加班審批', indent: true },
      { path: '/approvals/expense', icon: FiCreditCard, label: '費用審批', indent: true },
      { path: '/approvals/travel', icon: FiMapPin, label: '差旅審批', indent: true },
      { path: '/team/attendance', icon: FiClock, label: '團隊出勤' },
      { path: '/team/leave-calendar', icon: FiCalendar, label: '團隊請假行事曆' },
      { path: '/team/performance', icon: FiTarget, label: '團隊績效' },
      { path: '/performance/goals', icon: FiTarget, label: '團隊目標' },
      { path: '/team/members', icon: FiUsers, label: '下屬資料' },
      { path: '/team/delegation', icon: FiRefreshCw, label: '代理設定' },
    ],
  },

  company: {
    label: '公司管理',
    flat: false,
    groups: [
      {
        id: 'company_hr',
        label: '人事行政',
        icon: FiUsers,
        defaultOpen: true,
        items: [
          { path: '/employees', icon: FiUsers, label: '員工主檔', roles: MANAGER_UP },
          { path: '/org-chart', icon: FiUsers, label: '組織架構', roles: MANAGER_UP },
          { path: '/departments', icon: FiGrid, label: '部門管理', roles: HR_ADMIN },
          { path: '/positions', icon: FiBriefcase, label: '職位管理', roles: HR_ADMIN },
          { path: '/onboarding', icon: FiCheckSquare, label: '到職管理', roles: HR_ADMIN },
          { path: '/offboarding-records', icon: FiUserMinus, label: '離職管理', roles: HR_ADMIN },
          { path: '/transfers', icon: FiRepeat, label: '員工轉調', roles: HR_ADMIN },
        ],
      },
      {
        id: 'company_payroll',
        label: '薪酬福利',
        icon: FiDollarSign,
        defaultOpen: false,
        items: [
          { path: '/payroll', icon: FiDollarSign, label: '薪資管理', roles: HR_ADMIN },
          { path: '/salary-setup', icon: FiDollarSign, label: '員工薪資設定', roles: HR_ADMIN },
          { path: '/salary-bands', icon: FiBarChart2, label: '薪酬區間', roles: HR_ADMIN },
          { path: '/insurance', icon: FiShield, label: '勞健保管理', roles: HR_ADMIN },
          { path: '/tax-settings', icon: FiFileText, label: '扣稅設定', roles: HR_ADMIN },
          { path: '/bank-transfer', icon: FiCreditCard, label: 'ACH 匯款檔', roles: HR_ADMIN },
          { path: '/rewards-penalties', icon: FiAward, label: '獎懲管理', roles: HR_ADMIN },
          { path: '/government-forms', icon: FiFileText, label: '法定報表', roles: HR_ADMIN },
          { path: '/insurance-changes', icon: FiUpload, label: '勞健保異動申報', roles: HR_ADMIN },
          { path: '/benefits', icon: FiGift, label: '員工福利卡', roles: HR_ADMIN },
          { path: '/severance', icon: FiDollarSign, label: '資遣費管理', roles: HR_ADMIN },
        ],
      },
      {
        id: 'company_talent',
        label: '人才發展',
        icon: FiTrendingUp,
        defaultOpen: false,
        items: [
          { path: '/performance', icon: FiTarget, label: '績效週期' },
          { path: '/performance/kpis', icon: FiActivity, label: 'KPI 指標', roles: MANAGER_UP },
          { path: '/performance/nine-box', icon: FiGrid, label: '9-Box 矩陣', roles: MANAGER_UP },
          { path: '/performance/calibration', icon: FiBarChart2, label: '績效校準', roles: HR_ADMIN },
          { path: '/performance/bonus-rules', icon: FiDollarSign, label: '績效獎金', roles: HR_ADMIN },
          { path: '/pips', icon: FiAlertTriangle, label: '績效改善 PIP', roles: MANAGER_UP },
          { path: '/development', icon: FiTrendingUp, label: '員工發展' },
          { path: '/training', icon: FiBook, label: '培訓管理' },
          { path: '/succession-planning', icon: FiUsers, label: '接班人計畫', roles: HR_ADMIN },
        ],
      },
      {
        id: 'company_recruit',
        label: '招募',
        icon: FiBriefcase,
        defaultOpen: false,
        items: [
          { path: '/recruitment', icon: FiBriefcase, label: '招聘管理', roles: HR_ADMIN },
          { path: '/offer-letters', icon: FiFileText, label: 'Offer 管理', roles: HR_ADMIN },
        ],
      },
      {
        id: 'company_admin',
        label: '公司行政',
        icon: FiGrid,
        defaultOpen: false,
        items: [
          { path: '/announcements', icon: FiFileText, label: '公司公告發布' },
          { path: '/equipment', icon: FiPackage, label: '資產管理', roles: HR_ADMIN },
          { path: '/meeting-rooms', icon: FiMapPin, label: '會議室管理', roles: HR_ADMIN },
          { path: '/scheduling', icon: FiCalendar, label: '排班管理', roles: HR_ADMIN },
          { path: '/shift-types', icon: FiClock, label: '班別類型', roles: HR_ADMIN },
          { path: '/attendance-anomalies', icon: FiAlertTriangle, label: '考勤異常', roles: HR_ADMIN },
          { path: '/reports', icon: FiBarChart2, label: '報表分析', roles: MANAGER_UP },
        ],
      },
    ],
  },

  system: {
    label: '系統設定',
    flat: true,
    items: [
      { path: '/company-policies', icon: FiBook, label: '企業政策', roles: HR_ADMIN },
      { path: '/holiday-calendar', icon: FiCalendar, label: '假期行事曆', roles: HR_ADMIN },
      { path: '/approval-settings', icon: FiShield, label: '審批流程', roles: HR_ADMIN },
      { path: '/rbac', icon: FiLock, label: '角色權限', roles: ADMIN_ONLY },
      { path: '/profile-change-requests', icon: FiInbox, label: '變更申請', roles: HR_ADMIN },
      { path: '/scheduled-reports', icon: FiClock, label: '排程報表', roles: HR_ADMIN },
      { path: '/audit-logs', icon: FiFileText, label: '審計日誌', roles: HR_ADMIN },
      { path: '/leave-types', icon: FiCalendar, label: '假別管理', roles: HR_ADMIN },
      { path: '/settings', icon: FiSettings, label: '系統設定', roles: HR_ADMIN },
      { path: '/subscription', icon: FiStar, label: '訂閱方案', roles: ADMIN_ONLY },
    ],
  },
}

// Filter an item by role
const canSeeItem = (item, user) => {
  if (item.excludeRoles && user && item.excludeRoles.includes(user.role)) return false
  if (item.roles && (!user || !item.roles.includes(user.role))) return false
  return true
}

const SIDEBAR_EXPAND_KEY = 'hrms_sidebar_expanded_groups_v2'
const ACTIVE_VIEW_KEY = 'hrms_active_view'

const DashboardLayout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const { t } = useI18n()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // Available views based on user role
  const availableViews = useMemo(() => getAvailableViews(user), [user])

  // Fetch RBAC roles once so we can surface multi-role info in the header.
  // This is purely informational — permission evaluation still happens server-side.
  const [myRoles, setMyRoles] = useState([])
  useEffect(() => {
    let cancelled = false
    authService.getMyRoles()
      .then((resp) => {
        if (cancelled) return
        const list = resp?.data?.rbac_roles || resp?.rbac_roles || []
        setMyRoles(Array.isArray(list) ? list : [])
      })
      .catch(() => { /* non-fatal — header badge simply stays hidden */ })
    return () => { cancelled = true }
  }, [])

  // Active view (persisted)
  const [activeViewKey, setActiveViewKey] = useState(() => {
    try {
      const stored = localStorage.getItem(ACTIVE_VIEW_KEY)
      if (stored) return stored
    } catch (e) { /* ignore */ }
    return 'personal'
  })

  // Ensure active view is valid for this user
  useEffect(() => {
    if (!availableViews.some((v) => v.key === activeViewKey)) {
      setActiveViewKey('personal')
      try { localStorage.setItem(ACTIVE_VIEW_KEY, 'personal') } catch (e) { /* noop */ }
    }
  }, [availableViews, activeViewKey])

  const currentView = useMemo(
    () => availableViews.find((v) => v.key === activeViewKey) || availableViews[0],
    [availableViews, activeViewKey]
  )

  const switchView = useCallback((key) => {
    setActiveViewKey(key)
    try { localStorage.setItem(ACTIVE_VIEW_KEY, key) } catch (e) { /* noop */ }
    setViewDropdownOpen(false)
  }, [])

  // View dropdown
  const [viewDropdownOpen, setViewDropdownOpen] = useState(false)
  const viewDropdownRef = useRef(null)
  useEffect(() => {
    const handler = (e) => {
      if (viewDropdownRef.current && !viewDropdownRef.current.contains(e.target)) {
        setViewDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Group expand/collapse state for the "company" view
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_EXPAND_KEY)
      if (stored) return JSON.parse(stored)
    } catch (e) { /* ignore */ }
    const defaults = {}
    menuStructure.company.groups.forEach((g) => { defaults[g.id] = !!g.defaultOpen })
    return defaults
  })

  const toggleGroup = useCallback((gid) => {
    setExpandedGroups((prev) => {
      const next = { ...prev, [gid]: !prev[gid] }
      try { localStorage.setItem(SIDEBAR_EXPAND_KEY, JSON.stringify(next)) } catch (e) { /* noop */ }
      return next
    })
  }, [])

  // Auto-expand the group containing the currently active route (company view only)
  useEffect(() => {
    if (activeViewKey !== 'company') return
    const match = menuStructure.company.groups.find((g) =>
      g.items.some((it) => it.path === location.pathname)
    )
    if (match && !expandedGroups[match.id]) {
      setExpandedGroups((prev) => {
        const next = { ...prev, [match.id]: true }
        try { localStorage.setItem(SIDEBAR_EXPAND_KEY, JSON.stringify(next)) } catch (e) { /* noop */ }
        return next
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, activeViewKey])

  const handleLogout = useCallback(async () => {
    await dispatch(logout())
    navigate('/login', { replace: true })
  }, [dispatch, navigate])

  const isActive = useCallback((path) => location.pathname === path, [location.pathname])

  const closeSidebarOnMobile = useCallback(() => {
    if (window.innerWidth < 1024) setSidebarOpen(false)
  }, [])

  const todayDate = useMemo(() => {
    return new Date().toLocaleDateString('zh-TW', {
      year: 'numeric', month: 'long', day: 'numeric',
    })
  }, [])

  // ---- Render helpers ----
  const renderItem = (item, indent = false) => (
    <Link
      key={item.path + (item.label || '')}
      to={item.path}
      onClick={closeSidebarOnMobile}
      className={`flex items-center gap-3 ${indent ? 'pl-10 pr-3' : 'px-4'} py-2 rounded-lg transition text-sm ${
        isActive(item.path)
          ? 'bg-blue-50 text-blue-600 font-semibold'
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
      }`}
    >
      <item.icon className={`${indent ? 'w-4 h-4' : 'w-5 h-5'} flex-shrink-0`} />
      <span className="truncate">{item.i18nKey ? t(item.i18nKey) : item.label}</span>
    </Link>
  )

  const renderFlatView = (viewDef) => {
    const visibleItems = viewDef.items.filter((it) => canSeeItem(it, user))
    return (
      <div className="space-y-1">
        {visibleItems.map((item) => renderItem(item, !!item.indent))}
      </div>
    )
  }

  const renderGroupedView = (viewDef) => {
    const visibleGroups = viewDef.groups
      .map((g) => ({ ...g, items: g.items.filter((it) => canSeeItem(it, user)) }))
      .filter((g) => g.items.length > 0)

    return (
      <div className="space-y-1">
        {visibleGroups.map((group) => {
          const isOpen = !!expandedGroups[group.id]
          const GroupIcon = group.icon
          const hasActive = group.items.some((it) => it.path === location.pathname)
          return (
            <div key={group.id} className="mb-1">
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition text-xs font-semibold uppercase tracking-wide ${
                  hasActive ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {GroupIcon && <GroupIcon className="w-4 h-4" />}
                <span className="flex-1 text-left">{group.label}</span>
                {isOpen ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
              </button>
              {isOpen && (
                <div className="space-y-0.5 mt-0.5">
                  {group.items.map((item) => renderItem(item, true))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  const currentMenu = menuStructure[activeViewKey] || menuStructure.personal

  const CurrentViewIcon = currentView?.icon || FiUser

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Offline status banner */}
      <OfflineBanner />

      {/* Global Command Palette (Cmd+K) */}
      <CommandPalette />

      {/* Mobile Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-screen transition-transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } bg-white border-r border-gray-200 w-64 lg:translate-x-0`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-5 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-gray-900">HRMS 系統</h1>
              <p className="text-xs text-gray-500 mt-0.5">
                {currentView?.emoji} {currentMenu.label}
              </p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 rounded-lg hover:bg-gray-100 lg:hidden"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-3">
            {currentMenu.flat ? renderFlatView(currentMenu) : renderGroupedView(currentMenu)}
          </nav>

          {/* User Menu */}
          <div className="p-3 border-t border-gray-200">
            <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50 mb-2">
              <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                {user?.employee?.first_name?.[0] || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.employee?.first_name} {user?.employee?.last_name}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2 w-full rounded-lg text-red-600 hover:bg-red-50 transition text-sm"
            >
              <FiLogOut className="w-4 h-4" />
              <span className="font-medium">{t('nav.logout')}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64 transition-all">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
          <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 transition lg:hidden"
              >
                <FiMenu className="w-6 h-6" />
              </button>
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 lg:hidden">
                HRMS
              </h2>

              {/* Multi-role badge: only shown when the user has extra RBAC-assigned roles
                  beyond their primary system role. Purely informational for now. */}
              {myRoles.length > 0 && (() => {
                const primaryLabel = PRIMARY_ROLE_LABELS[user?.role] || user?.role || '未知'
                const extraLabels = myRoles.map((r) => r.role_name).join('、')
                const tooltip = `目前角色：${primaryLabel} + ${extraLabels}`
                return (
                  <div
                    className="hidden md:flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded text-xs"
                    title={tooltip}
                  >
                    <FiLock className="w-3 h-3" />
                    <span className="font-medium">{primaryLabel}</span>
                    <span className="text-indigo-400">+{myRoles.length}</span>
                  </div>
                )
              })()}

              {/* Role / View Switcher */}
              {availableViews.length > 1 && (
                <div className="relative" ref={viewDropdownRef}>
                  <button
                    onClick={() => setViewDropdownOpen(!viewDropdownOpen)}
                    className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition"
                  >
                    <CurrentViewIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">{currentView?.emoji} {currentView?.label}視角</span>
                    <span className="sm:hidden">{currentView?.emoji}</span>
                    <FiChevronDown className="w-4 h-4" />
                  </button>
                  {viewDropdownOpen && (
                    <div className="absolute left-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
                      {availableViews.map((v) => {
                        const Icon = v.icon
                        const active = v.key === currentView?.key
                        return (
                          <button
                            key={v.key}
                            onClick={() => switchView(v.key)}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50 flex items-center gap-2 ${
                              active ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <span>{v.emoji} {v.label}視角</span>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              <button
                onClick={() => { document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true })) }}
                className="hidden lg:flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-500 hover:border-gray-400 hover:bg-gray-50 transition"
                title="Cmd+K"
              >
                <FiSearch className="w-4 h-4" /> 搜尋…
                <kbd className="text-[10px] px-1 py-0.5 bg-gray-100 border border-gray-200 rounded">⌘K</kbd>
              </button>
              <span className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                {todayDate}
              </span>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-sm lg:hidden">
                {user?.employee?.first_name?.[0] || 'U'}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default DashboardLayout
